import { useState, useEffect } from "react";
import { FaBoxOpen, FaPlus } from "react-icons/fa6";
import { LogisticItemService } from "../../../../../services/LogisticItemService";
import { LogisticCategoryService } from "../../../../../services/LogisticCategoryService";
import { ModalWraperLogistic } from "./wraper";
import { SelectLogisticCategory } from "../../../../../components/forms/selects/SelectLogisticCategory";
import { InputDefault } from "../../../../../components/forms/InputDefault";

export const CreateNewLogisticItemModal = ({
    refresh,
    openModal,
    setOpenModal,
}) => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [categories, setCategories] = useState([]);

    const [formData, setFormData] = useState({
        name: "",
        type: "",
        category_id: "",
        unit: "pcs",
        min_stock: 10,
        total_stock: 0,
    });

    // Fetch Kategori saat modal dibuka
    useEffect(() => {
        if (openModal) {
            LogisticCategoryService.get({ params: {} })
                .then((res) => setCategories(res?.data?.data ?? []))
                .catch((err) => console.error("Gagal load kategori:", err));
        }
    }, [openModal]);

    const closeModal = () => {
        setOpenModal(false);
        setFormData({
            name: "",
            type: "",
            category_id: "",
            unit: "pcs",
            min_stock: 10,
            total_stock: 0,
        });
        setErrors({});
        setLoading(false);
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Nama barang harus diisi";
        }

        if (!formData.unit.trim()) {
            newErrors.unit = "Satuan harus diisi (contoh: pcs, meter, roll)";
        }

        if (formData.min_stock === "" || formData.min_stock < 0) {
            newErrors.min_stock = "Stok minimum tidak boleh kosong atau negatif";
        }

        if (formData.total_stock === "" || formData.total_stock < 0) {
            newErrors.total_stock = "Stok awal tidak boleh negatif";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);
            setErrors({});

            await LogisticItemService.store({
                data: {
                    name: formData.name,
                    type: formData.type || null,
                    category_id: formData.category_id || null,
                    unit: formData.unit,
                    min_stock: parseInt(formData.min_stock),
                    total_stock: parseInt(formData.total_stock) || 0,
                },
            });

            if (refresh) refresh();
            closeModal();
        } catch (err) {
            console.error("Gagal menyimpan data barang:", err);

            const serverErrors = err?.response?.data;

            if (typeof serverErrors === "object" && serverErrors !== null) {
                const formatted = {};
                for (const [key, value] of Object.entries(serverErrors)) {
                    formatted[key] = Array.isArray(value) ? value[0] : value;
                }
                setErrors(formatted);
            } else if (serverErrors?.message) {
                setErrors({ general: serverErrors.message });
            } else {
                setErrors({ general: "Terjadi kesalahan saat menyimpan data" });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <ModalWraperLogistic
            isOpen={openModal}
            setIsOpen={setOpenModal}
            title="Tambah Barang Baru"
        >
            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Error General */}
                {errors.general && (
                    <div
                        className="px-4 py-3 rounded-xl text-sm text-red-400 border border-red-500/20"
                        style={{ background: "rgba(239, 68, 68, 0.06)" }}
                    >
                        {errors.general}
                    </div>
                )}

                {/* Kategori */}
                <SelectLogisticCategory
                    label="Kategori"
                    value={formData.category_id}
                    setValue={(val) => setFormData((prev) => ({ ...prev, category_id: val }))}
                    placeholder="Pilih Kategori"
                />

                {/* Nama Barang */}
                <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
                        Nama Barang <span className="text-red-400">*</span>
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                            setFormData((prev) => ({ ...prev, name: e.target.value }))
                        }
                        placeholder="Contoh: Kabel Fiber Optik 12 Core"
                        className={`w-full px-4 py-3 text-sm rounded-xl bg-slate-800/60 border text-gray-100 placeholder-slate-500 outline-none transition-all focus:ring-1 ${errors.name
                                ? "border-red-500/50 focus:border-red-500 focus:ring-red-500/20"
                                : "border-slate-600/40 focus:border-cyan-500/50 focus:ring-cyan-500/20"
                            }`}
                    />
                    {errors.name && (
                        <p className="text-xs text-red-400 mt-1.5">{errors.name}</p>
                    )}
                </div>

                {/* Jenis / Tipe & Satuan */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
                            Jenis / Tipe
                        </label>
                        <input
                            type="text"
                            value={formData.type}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, type: e.target.value }))
                            }
                            placeholder="Contoh: Single Mode"
                            className="w-full px-4 py-3 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
                            Satuan <span className="text-red-400">*</span>
                        </label>
                        <select
                            value={formData.unit}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, unit: e.target.value }))
                            }
                            className={`w-full px-4 py-3 text-sm rounded-xl bg-slate-800/60 border text-gray-100 outline-none transition-all focus:ring-1 appearance-none cursor-pointer ${errors.unit
                                    ? "border-red-500/50 focus:border-red-500 focus:ring-red-500/20"
                                    : "border-slate-600/40 focus:border-cyan-500/50 focus:ring-cyan-500/20"
                                }`}
                            style={{
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E")`,
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 12px center",
                            }}
                        >
                            <option value="pcs">Pcs (Buah)</option>
                            <option value="meter">Meter</option>
                            <option value="roll">Roll</option>
                            <option value="bh">Bh (Buah)</option>
                            <option value="set">Set</option>
                            <option value="lusin">Lusin</option>
                            <option value="rim">Rim</option>
                        </select>
                        {errors.unit && (
                            <p className="text-xs text-red-400 mt-1.5">{errors.unit}</p>
                        )}
                    </div>
                </div>

                {/* Stok Minimum & Stok Awal */}
                <div className="grid grid-cols-2 gap-4">
                    <InputDefault
                        id="min_stock"
                        label="Stok Minimum"
                        type="number"
                        placeholder="10"
                        value={formData.min_stock}
                        onChange={(e) =>
                            setFormData((prev) => ({ ...prev, min_stock: e.target.value }))
                        }
                        error={errors.min_stock}
                        disabled={loading}
                    />

                    <InputDefault
                        id="total_stock"
                        label="Stok Awal"
                        type="number"
                        placeholder="0"
                        value={formData.total_stock}
                        onChange={(e) =>
                            setFormData((prev) => ({ ...prev, total_stock: e.target.value }))
                        }
                        error={errors.total_stock}
                        disabled={loading}
                    />
                </div>

                {/* Info Ringkasan */}
                {formData.name && (
                    <div
                        className="rounded-xl p-4 space-y-2"
                        style={{
                            background: "rgba(0, 180, 216, 0.04)",
                            border: "1px solid rgba(0, 180, 216, 0.1)",
                        }}
                    >
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                            Ringkasan Data
                        </p>
                        <p className="text-sm text-slate-300">
                            Barang <span className="font-semibold text-cyan-400">"{formData.name}"</span> akan didaftarkan
                            {formData.total_stock > 0 && (
                                <span> dengan stok awal sebanyak <span className="font-semibold text-cyan-400">{formData.total_stock} {formData.unit}</span></span>
                            )}
                            {formData.min_stock > 0 && (
                                <span> dan batas peringatan di <span className="font-semibold text-amber-400">{formData.min_stock} {formData.unit}</span></span>
                            )}
                            .
                        </p>
                    </div>
                )}

                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                    <button
                        type="button"
                        onClick={closeModal}
                        className="flex-1 py-3 rounded-xl border border-slate-700 text-slate-300 font-medium transition-colors hover:bg-slate-800"
                    >
                        Batal
                    </button>

                    <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold flex items-center justify-center gap-2 transition-colors"
                    >
                        {loading ? (
                            <>
                                <svg
                                    className="animate-spin h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    />
                                </svg>
                                Menyimpan...
                            </>
                        ) : (
                            <>
                                <FaPlus className="text-sm" />
                                Simpan Data
                            </>
                        )}
                    </button>
                </div>
            </form>
        </ModalWraperLogistic>
    );
};